/* eslint-disable @typescript-eslint/no-explicit-any */
import { type PortalTransport, type PortalChannel, PortalEventType, PORTAL } from './portal';
import type { PortalEventPayloadMap } from './portal';
import type { SequenceID } from './sequence';
import type { PortalService } from './service';

export function createChannelDelegate<T>(
    channelName: string,
    transport: PortalTransport,
    readyPromise: Promise<void>,
): PortalChannel<T> {
    const subscribers: ((data: any) => void)[] = [];

    transport.receive(PortalEventType.Channel, (event) => {
        if (event.$owner !== PORTAL || event.payload.channelName !== channelName) {
            return;
        }

        for (const subscriber of subscribers) {
            subscriber(event.payload.data);
        }
    });

    return {
        publish(data: any) {
            readyPromise.then(() => {
                transport.send({
                    type: PortalEventType.Channel,
                    payload: { channelName, data },
                    $owner: PORTAL,
                });
            });
        },

        subscribe(handler: (data: any) => void) {
            subscribers.push(handler);
            return () => {
                const index = subscribers.indexOf(handler);
                if (index >= 0) {
                    subscribers.splice(index, 1);
                }
            };
        },
    };
}

export function createServiceDelegate<T extends PortalService<any>>(
    serviceName: string,
    transport: PortalTransport,
    sequence: SequenceID,
    readyPromise: Promise<void>,
): T {
    return new Proxy(
        {},
        {
            get(target, apiName) {
                return (...args: any[]) => {
                    const seq = sequence.next();
                    readyPromise
                        .then(() => {
                            transport.send({
                                type: PortalEventType.Request,
                                payload: { seq, serviceName, apiName: String(apiName), args },
                                $owner: PORTAL,
                            });
                        })
                        .catch(() => {
                            transport.send({
                                type: PortalEventType.Request,
                                payload: {
                                    seq,
                                    serviceName,
                                    apiName: String(apiName),
                                    args: JSON.parse(JSON.stringify(args)),
                                },
                                $owner: PORTAL,
                            });
                        });

                    return new Promise((resolve, reject) => {
                        const unsubscribe = transport.receive(PortalEventType.Response, (event) => {
                            if (event.$owner !== PORTAL || event.payload.seq !== seq) {
                                return;
                            }

                            unsubscribe();
                            const { error, data } = event.payload as PortalEventPayloadMap[PortalEventType.Response];
                            if (error) {
                                reject(new Error(error));
                            } else {
                                resolve(data);
                            }
                        });
                    });
                };
            },
        },
    ) as T;
}
